import React, { useEffect, useState } from "react";
import { User, Building2, AlertCircle, CheckCircle2, ArrowLeft, Save } from "lucide-react";
import { authService } from '../../services/authService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function DatoscuentaPage({ setActivePage }) {
  // ✅ Estados organizados
  const [tiposCuenta, setTiposCuenta] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [erroresValidacion, setErroresValidacion] = useState({});
  
  // Mapeo de ciudades por departamento
  const ciudadesPorDepartamento = {
    '05': [ 
      { code: '05001', name: 'Medellín' }, 
      { code: '05002', name: 'Abejorral' },
      { code: '05088', name: 'Bello' },
      { code: '05129', name: 'Caldas' },
      { code: '05266', name: 'Envigado' },
      { code: '05380', name: 'Itagüí' },
      { code: '05631', name: 'Sabaneta' }
    ],
    '76': [ 
      { code: '76001', name: 'Cali' }, 
      { code: '76109', name: 'Alcalá' },
      { code: '76834', name: 'Tuluá' },
      { code: '76892', name: 'Yumbo' }
    ],
    '11': [ { code: '11001', name: 'Bogotá, D.C.' } ],
    '13': [
      { code: '13001', name: 'Cartagena' },
      { code: '13434', name: 'Magangué' }
    ],
    '68': [
      { code: '68001', name: 'Bucaramanga' },
      { code: '68276', name: 'Floridablanca' },
      { code: '68307', name: 'Girón' }
    ]
  };

  // Estado de la cuenta
  const [estadoCuenta, setEstadoCuenta] = useState('nueva');
  const [logoEmpresa, setLogoEmpresa] = useState("");

  const [cuenta, setCuenta] = useState({
    tipoCuenta: '',
    tipoDocumento: '',
    numeroIdentificacion: '',
    digitoVerificador: '',
    razonSocial: '',
    primerApellido: '',
    segundoApellido: '',
    nombres: '',
    direccion: '',
    pais: 'Colombia',
    departamento: '',
    ciudad: '',
    codigoPostal: '',
    ciiu: '',
    telefono: '',
    correo: '',
    correoFacturacion: '',
  });

  const [aceptaPolitica, setAceptaPolitica] = useState(false);

  // ✅ Inicializar datos
  useEffect(() => {
    setTiposCuenta([
      { value: 'natural', label: 'Persona Natural' },
      { value: 'juridica', label: 'Persona Jurídica' }
    ]);
    
    setTiposDocumento([
      { value: 'nit', label: 'NIT' },
      { value: 'cc', label: 'Cédula de Ciudadanía' },
      { value: 'ce', label: 'Cédula de Extranjería' },
      { value: 'ti', label: 'Tarjeta de Identidad' },
    ]);
    
    setDepartamentos([
      { code: '05', name: 'Antioquia' },
      { code: '76', name: 'Valle del Cauca' },
      { code: '11', name: 'Bogotá, D.C.' },
      { code: '13', name: 'Bolívar' },
      { code: '68', name: 'Santander' }
    ]);
  }, []);

  // ✅ Actualizar ciudades cuando cambie departamento
  useEffect(() => {
    if (cuenta.departamento && ciudadesPorDepartamento[cuenta.departamento]) {
      setCiudades(ciudadesPorDepartamento[cuenta.departamento]);
      // Limpiar ciudad seleccionada al cambiar departamento
      if (cuenta.ciudad) {
        setCuenta(prev => ({ ...prev, ciudad: '' }));
      }
    } else {
      setCiudades([]);
    }
  }, [cuenta.departamento]);

  // ✅ Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCuenta(prev => ({ ...prev, [name]: value }));
    
    // Limpiar error de validación al escribir
    if (erroresValidacion[name]) {
      setErroresValidacion(prev => {
        const nuevos = { ...prev };
        delete nuevos[name];
        return nuevos;
      });
    }
  };

  // ✅ Validaciones mejoradas
  const validaciones = {
    tipoCuenta: (val) => !!val,
    tipoDocumento: (val) => !!val,
    numeroIdentificacion: (val) => /^[0-9]{6,15}$/.test(val),
    digitoVerificador: (val) => /^[0-9]$/.test(val),
    razonSocial: (val) => cuenta.tipoCuenta !== "juridica" || (val && val.trim().length >= 3),
    primerApellido: (val) => cuenta.tipoCuenta !== "natural" || (val && val.trim().length >= 2),
    nombres: (val) => cuenta.tipoCuenta !== "natural" || (val && val.trim().length >= 2),
    direccion: (val) => val && val.trim().length >= 5 && !/[#\-"]/.test(val),
    departamento: (val) => !!val,
    ciudad: (val) => !!val,
    codigoPostal: (val) => /^[0-9]{4,6}$/.test(val),
    telefono: (val) => /^[0-9]{7,10}$/.test(val),
    correo: (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val),
    correoFacturacion: (val) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(val),
    ciiu: (val) => /^[0-9]{4}$/.test(val),
  };

  // ✅ Mensajes de error personalizados
  const mensajesError = {
    tipoCuenta: "Debe seleccionar un tipo de cuenta",
    tipoDocumento: "Debe seleccionar un tipo de documento",
    numeroIdentificacion: "Debe tener entre 6 y 15 dígitos",
    digitoVerificador: "Debe ser un dígito del 0 al 9",
    razonSocial: "Razón social debe tener al menos 3 caracteres",
    primerApellido: "Primer apellido debe tener al menos 2 caracteres",
    nombres: "Nombres debe tener al menos 2 caracteres",
    direccion: "Dirección debe tener al menos 5 caracteres y no contener #, - o \"",
    departamento: "Debe seleccionar un departamento",
    ciudad: "Debe seleccionar una ciudad",
    codigoPostal: "Debe tener entre 4 y 6 dígitos",
    telefono: "Debe tener entre 7 y 10 dígitos",
    correo: "Debe ser un correo electrónico válido",
    correoFacturacion: "Debe ser un correo electrónico válido",
    ciiu: "Código CIIU debe tener exactamente 4 dígitos",
  };

  // ✅ Validar formulario completo
  const validarFormulario = () => {
    const errores = {};
    
    Object.keys(validaciones).forEach(campo => {
      if (!validaciones[campo](cuenta[campo])) {
        errores[campo] = mensajesError[campo];
      }
    });

    if (!aceptaPolitica) {
      errores.politica = "Debe aceptar la política de tratamiento de datos";
    }
    
    setErroresValidacion(errores);
    return Object.keys(errores).length === 0;
  };

  // ✅ Guardar datos usando authService
  const guardarDatos = async (e) => {
    e.preventDefault();

    // Validar formulario
    if (!validarFormulario()) {
      toast.error("Por favor corrige los errores en el formulario");
      return;
    }

    setIsLoading(true);

    // ✅ Preparar payload
    const payload = {
      accountType: cuenta.tipoCuenta === 'natural' ? 'Persona Natural' : 'Persona Jurídica',
      documentType: cuenta.tipoDocumento,
      identificationNumber: parseInt(cuenta.numeroIdentificacion),
      verificationDigit: parseInt(cuenta.digitoVerificador),
      companyName: cuenta.razonSocial || null,
      firstName: cuenta.nombres || null,
      firstLastName: cuenta.primerApellido || null,
      middleLastName: cuenta.segundoApellido || null,
      address: cuenta.direccion,
      country: cuenta.pais,
      state: departamentos.find(dep => dep.code === cuenta.departamento)?.name || '',
      city: ciudades.find(ciu => ciu.code === cuenta.ciudad)?.name || '',
      postalCode: cuenta.codigoPostal,
      economicActivity: parseInt(cuenta.ciiu),
      phone: cuenta.telefono,
      accountEmail: cuenta.correo,
      billingEmail: cuenta.correoFacturacion,
      acceptsPolicies: aceptaPolitica,
      ip: "127.0.0.1" // Se podría obtener dinámicamente
    };

    try {
      console.log('📤 Enviando datos de registro...', payload);
      
      // ✅ Usar authService en lugar de fetch directo
      const response = await authService.RegisterUserApi(payload);
      
      console.log('✅ Respuesta del servidor:', response);

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success("🎉 Cuenta creada exitosamente. Te enviamos un email de confirmación.");
        
        // Limpiar formulario
        setCuenta({
          tipoCuenta: '',
          tipoDocumento: '',
          numeroIdentificacion: '',
          digitoVerificador: '',
          razonSocial: '',
          primerApellido: '',
          segundoApellido: '',
          nombres: '',
          direccion: '',
          pais: 'Colombia',
          departamento: '',
          ciudad: '',
          codigoPostal: '',
          ciiu: '',
          telefono: '',
          correo: '',
          correoFacturacion: '',
        });
        setAceptaPolitica(false);
        setErroresValidacion({});

        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          if (setActivePage) {
            setActivePage('login');
          } else {
            window.location.reload();
          }
        }, 3000);
      } else {
        throw new Error(response?.message || "Error inesperado del servidor");
      }

    } catch (error) {
      console.error("❌ Error al crear cuenta:", error);
      
      let errorMessage = "Error al crear la cuenta";
      
      // ✅ Manejo mejorado de errores
      if (error.type === 'network') {
        errorMessage = "Error de conexión. Verifica tu internet o contacta al administrador.";
      } else if (error.type === 'server') {
        errorMessage = error.message || "Error del servidor";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Componente para mostrar errores de campo
  const ErrorField = ({ campo }) => {
    if (!erroresValidacion[campo]) return null;
    
    return (
      <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
        <AlertCircle className="w-3 h-3" />
        <span>{erroresValidacion[campo]}</span>
      </div>
    );
  };

  // ✅ Botón para volver al login
  const volverAlLogin = () => {
    if (setActivePage) {
      setActivePage('login');
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center py-8">
      <div className="bg-white w-full max-w-4xl mx-auto rounded-2xl shadow-xl px-8 py-8 border border-gray-200">
        
        {/* ✅ Header mejorado */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="bg-[#003865]/10 rounded-full p-3 flex items-center justify-center w-16 h-16">
              {logoEmpresa ? (
                <img
                  src={logoEmpresa}
                  alt="Logo de la empresa"
                  className="w-12 h-12 object-contain rounded-full"
                />
              ) : (
                <Building2 className="h-10 w-10 text-[#003865]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#003865]">
                Crear Nueva Cuenta
              </h1>
              <p className="text-gray-600 text-sm">
                Información general de la empresa
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                estadoCuenta === 'nueva' ? 'bg-blue-100 text-blue-700' : 
                estadoCuenta === 'activa' ? 'bg-green-100 text-green-700' : 
                estadoCuenta === 'bloqueada' ? 'bg-red-100 text-red-700' : 
                'bg-yellow-100 text-yellow-800'
              }`}
            >
              {estadoCuenta.charAt(0).toUpperCase() + estadoCuenta.slice(1)}
            </span>
            
            <button
              onClick={volverAlLogin}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              title="Volver al login"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden md:inline">Volver</span>
            </button>
          </div>
        </div>

        {/* ✅ Formulario mejorado */}
        <form className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" onSubmit={guardarDatos}>
          
          {/* Tipo de Cuenta */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Tipo de Cuenta <span className="text-red-500">*</span>
            </label>
            <select
              name="tipoCuenta"
              value={cuenta.tipoCuenta}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.tipoCuenta ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Seleccione...</option>
              {tiposCuenta.map(tc => (
                <option key={tc.value} value={tc.value}>{tc.label}</option>
              ))}
            </select>
            <ErrorField campo="tipoCuenta" />
          </div>

          {/* Tipo de Documento */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Tipo de Documento <span className="text-red-500">*</span>
            </label>
            <select
              name="tipoDocumento"
              value={cuenta.tipoDocumento}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.tipoDocumento ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Seleccione...</option>
              {tiposDocumento.map(td => (
                <option key={td.value} value={td.value}>{td.label}</option>
              ))}
            </select>
            <ErrorField campo="tipoDocumento" />
          </div>

          {/* Número Identificación */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Número de Identificación <span className="text-red-500">*</span>
            </label>
            <input
              name="numeroIdentificacion"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={15}
              value={cuenta.numeroIdentificacion}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '');
                handleChange({ target: { name: 'numeroIdentificacion', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.numeroIdentificacion ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Ingrese el número"
              required
            />
            <ErrorField campo="numeroIdentificacion" />
          </div>

          {/* Dígito Verificador */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Dígito Verificador <span className="text-red-500">*</span>
            </label>
            <input
              name="digitoVerificador"
              type="text"
              inputMode="numeric"
              pattern="[0-9]{1}"
              maxLength={1}
              value={cuenta.digitoVerificador}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                handleChange({ target: { name: 'digitoVerificador', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.digitoVerificador ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="0-9"
              required
            />
            <ErrorField campo="digitoVerificador" />
          </div>

          {/* Razón Social (solo para Jurídica) */}
          {cuenta.tipoCuenta === "juridica" && (
            <div className="md:col-span-2">
              <label className="block font-semibold mb-2 text-gray-800">
                Razón Social <span className="text-red-500">*</span>
              </label>
              <input
                name="razonSocial"
                type="text"
                value={cuenta.razonSocial}
                onChange={handleChange}
                className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                  erroresValidacion.razonSocial ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                placeholder="Nombre de la empresa"
                required
              />
              <ErrorField campo="razonSocial" />
            </div>
          )}

          {/* Campos para Persona Natural */}
          {cuenta.tipoCuenta === "natural" && (
            <>
              <div>
                <label className="block font-semibold mb-2 text-gray-800">
                  Primer Apellido <span className="text-red-500">*</span>
                </label>
                <input
                  name="primerApellido"
                  type="text"
                  value={cuenta.primerApellido}
                  onChange={handleChange}
                  className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                    erroresValidacion.primerApellido ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Primer apellido"
                  required
                />
                <ErrorField campo="primerApellido" />
              </div>
              
              <div>
                <label className="block font-semibold mb-2 text-gray-800">
                  Segundo Apellido
                </label>
                <input
                  name="segundoApellido"
                  type="text"
                  value={cuenta.segundoApellido}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base"
                  placeholder="Segundo apellido (opcional)"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block font-semibold mb-2 text-gray-800">
                  Nombres <span className="text-red-500">*</span>
                </label>
                <input
                  name="nombres"
                  type="text"
                  value={cuenta.nombres}
                  onChange={handleChange}
                  className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                    erroresValidacion.nombres ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Nombres completos"
                  required
                />
                <ErrorField campo="nombres" />
              </div>
            </>
          )}

          {/* Dirección */}
          <div className="md:col-span-2">
            <label className="block font-semibold mb-2 text-gray-800">
              Dirección <span className="text-red-500">*</span>
            </label>
            <input
              name="direccion"
              type="text"
              value={cuenta.direccion}
              onChange={e => {
                const value = e.target.value.replace(/[#\-"]/g, '');
                handleChange({ target: { name: 'direccion', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.direccion ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="Calle 123 No 45 67"
              required
            />
            <ErrorField campo="direccion" />
          </div>

          {/* País */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              País <span className="text-red-500">*</span>
            </label>
            <select
              name="pais"
              value={cuenta.pais}
              className="border border-gray-300 rounded-lg h-12 px-3 w-full bg-gray-50 text-base cursor-not-allowed"
              disabled
            >
              <option value="Colombia">Colombia</option>
            </select>
          </div>

          {/* Departamento */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Departamento <span className="text-red-500">*</span>
            </label>
            <select
              name="departamento"
              value={cuenta.departamento}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.departamento ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              required
            >
              <option value="">Seleccione...</option>
              {departamentos.map(dep => (
                <option key={dep.code} value={dep.code}>{dep.name}</option>
              ))}
            </select>
            <ErrorField campo="departamento" />
          </div>

          {/* Ciudad */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Ciudad <span className="text-red-500">*</span>
            </label>
            <select
              name="ciudad"
              value={cuenta.ciudad}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.ciudad ? 'border-red-300 bg-red-50' : 'border-gray-300'
              } ${!cuenta.departamento ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              required
              disabled={!cuenta.departamento}
            >
              <option value="">
                {cuenta.departamento ? 'Seleccione...' : 'Primero seleccione departamento'}
              </option>
              {ciudades.map(c => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <ErrorField campo="ciudad" />
          </div>

          {/* Código Postal */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Código Postal <span className="text-red-500">*</span>
            </label>
            <input
              name="codigoPostal"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={cuenta.codigoPostal}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '');
                handleChange({ target: { name: 'codigoPostal', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.codigoPostal ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="050001"
              required
            />
            <ErrorField campo="codigoPostal" />
          </div>

          {/* CIIU */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Actividad Económica (CIIU) <span className="text-red-500">*</span>
            </label>
            <input
              name="ciiu"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={cuenta.ciiu}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                handleChange({ target: { name: 'ciiu', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.ciiu ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="4711"
              required
            />
            <ErrorField campo="ciiu" />
          </div>

          {/* Teléfono */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Teléfono <span className="text-red-500">*</span>
            </label>
            <input
              name="telefono"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              value={cuenta.telefono}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '');
                handleChange({ target: { name: 'telefono', value } });
              }}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.telefono ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="3001234567"
              required
            />
            <ErrorField campo="telefono" />
          </div>

          {/* Correo Electrónico */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Correo Electrónico <span className="text-red-500">*</span>
            </label>
            <input
              name="correo"
              type="email"
              value={cuenta.correo}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.correo ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="usuario@empresa.com"
              required
            />
            <ErrorField campo="correo" />
          </div>

          {/* Correo de Facturación */}
          <div>
            <label className="block font-semibold mb-2 text-gray-800">
              Correo de Facturación <span className="text-red-500">*</span>
            </label>
            <input
              name="correoFacturacion"
              type="email"
              value={cuenta.correoFacturacion}
              onChange={handleChange}
              className={`border rounded-lg h-12 px-3 w-full focus:ring-2 focus:ring-blue-200 text-base transition-colors ${
                erroresValidacion.correoFacturacion ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder="facturacion@empresa.com"
              required
            />
            <ErrorField campo="correoFacturacion" />
          </div>

          {/* Checkbox política */}
          <div className="md:col-span-2 mt-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input
                id="politica"
                type="checkbox"
                checked={aceptaPolitica}
                onChange={e => setAceptaPolitica(e.target.checked)}
                className="mt-1 rounded border-gray-300 text-[#003865] focus:ring-[#003865] focus:ring-2"
                required
              />
              <label htmlFor="politica" className="text-sm text-gray-800 cursor-pointer flex-1">
                Acepto la{' '}
                <a 
                  href="/politica-datos" 
                  target="_blank" 
                  className="text-blue-600 hover:text-blue-700 underline font-medium"
                >
                  Política de Tratamiento de Datos
                </a>{' '}
                y autorizo el procesamiento de mis datos personales conforme a la normativa vigente.
              </label>
            </div>
            <ErrorField campo="politica" />
          </div>

          {/* Botones de acción */}
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 mt-8">
            <button
              type="button"
              onClick={volverAlLogin}
              className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold"
              disabled={isLoading}
            >
              <ArrowLeft className="w-4 h-4" />
              Volver al Login
            </button>
            
            <button
              type="submit"
              disabled={isLoading || !aceptaPolitica}
              className={`flex-1 flex items-center justify-center gap-2 px-8 py-3 rounded-xl shadow font-bold text-base transition-all duration-200 ${
                isLoading || !aceptaPolitica
                  ? 'bg-gray-400 cursor-not-allowed text-white' 
                  : 'bg-[#003865] hover:bg-blue-700 text-white hover:shadow-lg transform hover:scale-[1.02]'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Crear Cuenta
                </>
              )}
            </button>
          </div>
        </form>
        
        {/* ✅ Toast container */}
        <ToastContainer 
          position="top-right" 
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </div>
  );
}
